import { useRef, useState, useEffect } from "react";
import { DialogContent, Button, Divider, Paper, Tooltip, TextField, Grid, Box, IconButton } from "@mui/material";
import { FileDownloadOutlined, FileUploadOutlined, Inventory2Outlined, RemoveCircleOutline, Save } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { updateActiveTask, Util } from "../../util/Util";
import InventoryImportSummary from "./InventoryImportSummary";
import {
	InventoryImportSummary as ImportSummary,
	InventorySheetError,
	InventorySheetLabels,
	applyInventoryImport,
	buildInventoryWorkbook,
	inventorySheetFileName,
	parseInventoryWorkbook,
} from "./inventorySheet";
import { mapTasksWithActivation, refreshEmptyActiveTaskDate } from "../../util/taskActivation";
import { loadPendingTasks, persistPendingTasks, revivePendingItems, useSkipFirstRun } from "../../util/pendingTaskStorage";
import { usePendingWorkSessions } from "../../hooks/usePendingWorkSessions";
import { flushItemTableItems } from "../../util/itemTableFlush";
import ConfirmDialog from "../../components/ConfirmDialog";
import TaskPane from "../../components/TaskPane";

import { useAuth } from "../../hooks/useAuth";
import { useDepotAccess } from "../../hooks/useDepotAccess";
import { toast } from "react-toastify";
import { InventoryTask, Item, useInventory } from './InventoryContext';
import { LocalStorageKeys, MYSQL_LIMITS, OperationEnum } from "../../util/constants";
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ItemTable from "./ItemTable";
import { useUI } from "../../providers/MoleUIContext";

import InventoryDetail from "./InventoryDetail";

import InventoryItemDetail from "./InventoryItemDetail";
import { v4 } from "uuid";
import { createDataGridSignCell } from "../../util/cellFormatters";
import { useAppSocket } from "../../providers/AppSocketContext";

import { addMoney } from "../../util/quantity";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
import { canAmend, canRecord } from '../../util/access';
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	onSuccess?: () => void;
}

const InventoryModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [tasksOpen, setTasksOpen] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);
	const [sheetBusy, setSheetBusy] = useState(false);
	const [importSummary, setImportSummary] = useState<{ summary: ImportSummary; fileName: string } | null>(null);
	const importInputRef = useRef<HTMLInputElement>(null);

	const { api, session, access } = useAuth();
	const { ensureDepotOperation } = useDepotAccess();
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { pendingTasks, setPendingTasks, fetchedTask, setFetchedTask, activeTask, addNewTask, setItems, items, apiRef } = useInventory();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});

	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const changeRef = useRef<boolean>(false);
	const skipFirstPersist = useSkipFirstRun();

	const reviveInventoryTask = (task: any): InventoryTask<Item> =>
		refreshEmptyActiveTaskDate({
			...task,
			createdAt: new Date(task.createdAt),
			updatedAt: new Date(task.updatedAt),
			items: revivePendingItems(task.items, (item: any) => new Item(item)),
			is_public: Boolean(task.is_public),
			server_v: Number(task.server_v || 0),
			total: function () {
				return (this.items ?? []).reduce((sum: number, item: { total: number }) => addMoney(sum, item.total), 0);
			},
		});

	const { userId, removeRemote, setPublic } = usePendingWorkSessions({
		workType: "inventory",
		enabled: Boolean(isOpen && !bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
		reviveTask: reviveInventoryTask,
		storageKey: LocalStorageKeys.INVENTORY_TASKS,
	});

	const { createdAt, updatedAt, active, items: itms, ...bundleDetails } = activeTask || {};

	const bundleItems = itms
		? Object.values(itms).map(({ ...rest }) => rest)
		: [];

	const canValidate = activeTask && Object.keys(activeTask.items!).length > 0
		&& validateDetail
		&& activeTask.date // TODO: validate this 
		&& (bundle ? taskHash !== Util.hashObject(JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)) : true);

	const fetchData = async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`/inventories/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				res.data.records.sort((a: any, b: any) => a.item_name.localeCompare(b.item_name));
				setFetchedTask(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		setLoadingMessage(null);
	};


	const onSubmit = async () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = async () => {
		if (!activeTask) return;

		if (!ensureDepotOperation(OperationEnum.INVENTORY, activeTask.depot)) return;

		const batch = bundle ? activeTask.id : v4();

		const details = {
			batch: batch,
			operation: OperationEnum.INVENTORY,
			receiver: activeTask.receiver,
			source_depot: activeTask.depot,
			dest_depot: activeTask.depot,
			rec_date: activeTask.date,
			ref: activeTask.reference,
			description: `${activeTask.description} - ${activeTask.invType}`,
			invType: activeTask.invType
		};

		const taskItems = flushItemTableItems<Item>(apiRef) ?? activeTask.items!;
		const records = taskItems.map(({ id, item, price, quantity }) => ({ item: id, item_desc: item, amount: price, quantity, batch: batch }));

		const payload = {
			details: details,
			records: records
		}

		setLoadingMessage(bundle ? "UPDATE" : "SAVE");

		const res = !bundle ? await api.post("/inventories", payload) : await api.put(`/inventories/${batch}`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				if (bundle) {
					toggle(); // Close modal
				} else {
					toast.success(t("common:toast.success.inventory_adjustment"), { autoClose: 3000 });
					deleteTask(activeTask.id, { posted: true }); // Posted: clear it for everyone, not just its owner
				}
				changeRef.current = true;
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
			} else {
				console.error("Failed to send data:", res);
			}
			// TODO: handle 400 bad requests
		}

		setLoadingMessage(null);
	}

	const deleteTask = (taskId: string, { posted = false }: { posted?: boolean } = {}) => {
		const existing = pendingTasks.find((task) => task.id === taskId);
		setPendingTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
		void removeRemote(taskId, existing?.server_v, { posted });
	};

	useEffect(() => {
		if (isOpen) {
			if (bundle) {
				addNewTask();
			} else {
				const parsedTasks = loadPendingTasks(LocalStorageKeys.INVENTORY_TASKS, reviveInventoryTask, session?.user_id);
				if (parsedTasks) {
					setPendingTasks(parsedTasks);
					return;
				}
				addNewTask();
			}
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !bundle) {
			const handleKeyDown = (e: KeyboardEvent) => {
				// Ctrl+Alt+Q — task pane (avoid bare Ctrl+Q: quits many browsers)
				if (!bundle && e.ctrlKey && e.altKey && e.key.toLowerCase() === "q") {
					e.preventDefault();
					setTasksOpen((open) => !open);
				} else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "s") {
					e.preventDefault();
					validateBtnRef.current?.click();
				}
			};

			document.addEventListener("keydown", handleKeyDown);

			return () => {
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [isOpen]);

	useEffect(() => {
		if (skipFirstPersist()) return;

		// Always persist pending task when not in edit mode (i.e., when bundle is not provided)
		if (!bundle && isOpen) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(LocalStorageKeys.INVENTORY_TASKS, session?.user_id, JSON.stringify(pendingTasks));
		}

		// If there are pending task and no active task, set the first task as active
		// This ensures that there is always an active task to work with
		// Empty tasks get today's date when activated if their date is stale
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prevTasks) =>
				mapTasksWithActivation(prevTasks, (_task, index) => index === 0)
			);
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);

			// Lazy fetch data if in edit mode and only one pending task exists
			// This is to ensure that the data is fetched only when there is a single task to edit
			// This is because calling it immediately after addNewTask might fetch when there are no tasks yet
			// because setting state is asynchronous
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle) fetchData();
				prevTaskIdRef.current = activeTask.id;
			}

			// If in edit mode, calculate the task hash
			// This is used to determine if the task details have changed
			// and if the update can proceed
			if (bundle && activeTask && bundleItems.length > 0 && !taskHash) {
				setTaskHash(
					Util.hashObject(
						JSON.stringify(bundleDetails)
						+
						JSON.stringify(bundleItems)
					)
				);
			}
		} else {
			prevTaskIdRef.current = null;
		}
	}, [activeTask]);

	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
		setTaskHash(null);
		if (changeRef.current) {
			onSuccess?.();
		}
	};

	// The count sheet serves an amendment as well as a fresh count: both edit the
	// same active task, and the depot's items are fetched either way. Amending
	// needs the update right; a fresh count takes either.
	const canUseSheet = bundle
		? canAmend(access, 'inventory')
		: (canRecord(access, 'inventory') || canAmend(access, 'inventory'));
	const sheetReady = !!activeTask && (activeTask.depot || -1) > 0 && items.length > 0 && !sheetBusy;

	const sheetLabels = (): InventorySheetLabels => {
		const s = (key: string) => t(`common:inventoryModal.sheet.${key}`);
		return {
			id: "id",
			rowNumber: "#",
			sector: t("common:tables.sector"),
			category: t("common:tables.category"),
			itemCode: t("common:tables.itemCode"),
			item: t("common:tables.item"),
			stock: t("common:tables.stock"),
			count: t("common:tables.count"),
			fractional: "fractional",
			unitValue: t("common:tables.unitValue"),
			stockDifference: t("common:tables.stockDifference"),
			stockValue: t("common:tables.stockValue"),
			countValue: t("common:tables.countValue"),
			valueDifference: t("common:tables.valueDifference"),
			inCount: s("inCount"),
			notCounted: s("notCounted"),
			total: t("common:tables.total"),
			info: {
				formatVersion: s("info.formatVersion"),
				taskId: s("info.taskId"),
				depotId: s("info.depotId"),
				depotName: t("common:tables.depot"),
				date: t("common:tables.date"),
				reference: t("common:tables.reference"),
				invType: s("info.invType"),
				exportedAt: s("info.exportedAt"),
				exportedBy: s("info.exportedBy"),
				rows: s("info.rows"),
				instructions: s("info.instructions"),
			},
			instructionsText: s("instructions"),
		};
	};

	const depotName = async (depotId: number): Promise<string> => {
		const res = await api.get("/depot/meterred");
		const row = res?.status === 200 ? (res.data as any[]).find((d) => d.id === depotId) : null;
		return row?.DepotName ?? "";
	};

	const onExportSheet = async () => {
		if (!activeTask || !sheetReady) return;
		setSheetBusy(true);
		try {
			const [{ default: ExcelJS }, name] = await Promise.all([import("exceljs"), depotName(activeTask.depot)]);
			const meta = {
				taskId: activeTask.id,
				depotId: activeTask.depot,
				depotName: name,
				date: activeTask.date,
				reference: activeTask.reference,
				invType: activeTask.invType,
				exportedAt: new Date().toISOString(),
				exportedBy: session?.username || "",
			};
			const workbook = await buildInventoryWorkbook(ExcelJS, {
				meta,
				taskLines: flushItemTableItems<Item>(apiRef) ?? activeTask.items ?? [],
				items,
				labels: sheetLabels(),
			});
			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = inventorySheetFileName(meta);
			document.body.appendChild(link);
			link.click();
			link.remove();
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch (error) {
			console.error("Inventory export failed:", error);
			toast.error(t("common:inventoryModal.sheet.exportError"), { autoClose: 3000 });
		} finally {
			setSheetBusy(false);
		}
	};

	const onImportFile = async (file: File | undefined) => {
		if (importInputRef.current) importInputRef.current.value = "";
		if (!file || !activeTask || !sheetReady) return;
		setSheetBusy(true);
		try {
			const { default: ExcelJS } = await import("exceljs");
			const workbook = new ExcelJS.Workbook();
			await workbook.xlsx.load(await file.arrayBuffer());
			const parsed = parseInventoryWorkbook(workbook);
			const taskLines = flushItemTableItems<Item>(apiRef) ?? activeTask.items ?? [];
			const result = applyInventoryImport({
				parsed,
				taskId: activeTask.id,
				depotId: activeTask.depot,
				taskLines,
				items,
			});
			if (result.status === "wrongDepot") {
				toast.error(
					t("common:inventoryModal.sheet.wrongDepot", { depot: result.fileDepotName || "?" }),
					{ autoClose: 5000 },
				);
				return;
			}
			if (result.taskMismatch && !window.confirm(t("common:inventoryModal.sheet.otherTask"))) {
				return;
			}
			updateActiveTask(setPendingTasks, { items: result.lines });
			setImportSummary({ summary: result.summary, fileName: file.name });
		} catch (error) {
			const code = error instanceof InventorySheetError ? error.code : "unreadable";
			console.error("Inventory import failed:", error);
			toast.error(t(`common:inventoryModal.sheet.importError.${code}`), { autoClose: 5000 });
		} finally {
			setSheetBusy(false);
		}
	};

	const toggleDrawer = (newOpen: boolean) => () => {
		setTasksOpen(newOpen);
	};

	const onDeleteClicked = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, prev => ({
			items: prev.items
				? prev.items.filter(item => item.uniqueId !== uniqueId)
				: [],
		}));
	};

	const columns: GridColDef<Item>[] = [
		{
			field: 'rowNumber',
			headerName: '#',
			width: 20,
			valueGetter: (_, row) => apiRef.current!.getRowIndexRelativeToVisibleRows(row.uniqueId) + 1,
		},
		{
			field: 'sector',
			headerName: t("common:tables.sector"),
		},
		{
			field: 'category',
			headerName: t("common:tables.category"),
		},
		{
			field: 'item_code',
			headerName: t("common:tables.itemCode"),
		},
		{
			field: 'item',
			headerName: t("common:tables.item"),
			flex: 2,
		},
		{
			field: 'stock',
			headerName: t('common:tables.stock'),
			type: 'number',
		},
		{
			field: 'quantity',
			headerName: t("common:tables.count"),
			type: 'number',
			editable: true
		},
		{
			field: 'price',
			headerName: t("common:tables.unitValue"),
			type: 'number',
			editable: true
		},
		{
			field: 'stockDifference',
			headerName: t("common:tables.stockDifference"),
			type: 'number',
			renderCell: createDataGridSignCell(),
		},
		{
			field: 'oldValue',
			headerName: t("common:tables.stockValue"),
			type: 'number',
		},
		{
			field: 'total',
			headerName: t("common:tables.countValue"),
			type: 'number',
		},
		{
			field: 'valueDifference',
			headerName: t("common:tables.valueDifference"),
			type: 'number',
			renderCell: createDataGridSignCell(),
		},
		{
			field: 'actions',
			headerName: '',
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<Item>) => (
				<Tooltip title={t("common:ui.tooltips.delete")}>
					<IconButton color="error" onClick={() => { onDeleteClicked(params.row.uniqueId) }}>
						<RemoveCircleOutline />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: "95%", width: "95%", maxHeight: "95vh" }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={t("common:inventoryModal.title")}
					subtitle={`${t("common:inventoryModal.totalValue")}: ${Util.formatCurrency(activeTask?.total() || 0, currency)}`}
					icon={<Inventory2Outlined />}
					onClose={toggle}
					themeColor={themeColor}
					extra={
						canUseSheet || !bundle ? (
							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
								{canUseSheet && (
									<>
										<Tooltip title={t("common:inventoryModal.sheet.exportHint")}>
											<span>
												<Button
													variant="outlined"
													size="small"
													startIcon={<FileDownloadOutlined />}
													onClick={onExportSheet}
													disabled={!sheetReady}
												>
													{t("common:inventoryModal.sheet.export")}
												</Button>
											</span>
										</Tooltip>
										<Tooltip title={t("common:inventoryModal.sheet.importHint")}>
											<span>
												<Button
													variant="outlined"
													size="small"
													startIcon={<FileUploadOutlined />}
													onClick={() => importInputRef.current?.click()}
													disabled={!sheetReady}
												>
													{t("common:inventoryModal.sheet.import")}
												</Button>
											</span>
										</Tooltip>
										<input
											ref={importInputRef}
											type="file"
											accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
											hidden
											onChange={(e) => onImportFile(e.target.files?.[0])}
										/>
									</>
								)}
								{/* Pending tasks are not persisted while amending, so the
								    drawer stays out of edit mode. */}
								{!bundle && (
									<Button
										variant="outlined"
										size="small"
										startIcon={<PendingActionsIcon />}
										onClick={() => toggleDrawer(true)()}
									>
										{t("common:ui.buttons.tasks")}
									</Button>
								)}
							</Box>
						) : undefined
					}
				/>
				<DialogContent>
					<Paper elevation={24}>
						{renderChildren &&
							<InventoryDetail
								setValidateCheck={setValidateDetail}
							/>
						}
						<Divider orientation="vertical" variant="middle" flexItem />
						{renderChildren &&
							<InventoryItemDetail
								bundle={fetchedTask?.records}
							/>
						}
					</Paper>
					{renderChildren &&
						<ItemTable<Item, InventoryTask<Item>>
							activeTask={activeTask}
							setPendingTasks={setPendingTasks}
							columns={columns}
							itemConstructor={Item}
							bundle={fetchedTask?.records}
							items={items}
							apiRef={apiRef}
						/>
					}
				</DialogContent>
				<MoleDialogActions sx={{ justifyContent: "stretch" }}>
					<Grid container spacing={1} alignItems="center" width="100%">
						<Grid sx={{ flexGrow: 1, minWidth: 0 }}>
							<TextField
								id="details"
								size="small"
								label={t("common:ui.labels.notes")}
								variant="filled"
								fullWidth
								type="text"
								value={activeTask?.description || ""}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
								onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
								disabled={!activeTask}
							/>
						</Grid>
						<Grid
							sx={{
								width: { xs: "100%", sm: "100%", md: "auto" },
								display: "flex",
								justifyContent: { xs: "space-between", md: "flex-end" },
								alignItems: "center",
								gap: 1,
							}}
						>
							<Button
								ref={validateBtnRef}
								variant="contained"
								color="success"
								onClick={onSubmit}
								disabled={!canValidate}
								startIcon={<Save />}
							>
								{bundle ? t("common:ui.buttons.amendAdjustment") : t("common:ui.buttons.adjustInventory")}
							</Button>
							<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
						</Grid>
					</Grid>
				</MoleDialogActions>

				{!bundle &&
					<TaskPane<Item, InventoryTask<Item>>
						tasks={pendingTasks}
						setTasks={setPendingTasks}
						addNewTask={addNewTask}
						tasksOpen={tasksOpen}
						onCallback={() => setTasksOpen(false)}
						currentUserId={userId}
						onTogglePublic={(task) => { void setPublic(task, !task.is_public); }}
						onDeleteTask={(id: string) => deleteTask(id)}
					/>
				}
			</MoleDialog>

			<InventoryImportSummary
				open={!!importSummary}
				onClose={() => setImportSummary(null)}
				summary={importSummary?.summary ?? null}
				fileName={importSummary?.fileName}
			/>

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				< ConfirmDialog
					onConfirm={handleConfirm}
					isOpen={confirmModalOpen}
					setIsOpen={setConfirmModalOpen}
					title={t("common:dialog.inventoryConfirmation")}
					confirmBtnColor="success" />
			}
		</Box>
	);
};

export default InventoryModal;
